import debounce from 'lodash.debounce';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { eventBus } from '../../../bus/eventBus';
import ImprovedSelect from './ImprovedSelect';
import CreateProductModal from '../../Modal/CreateProductModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from 'react-hook-form';
import { API_BASE_URL } from '../../../api';

const PAGE_SIZE = 20;

const formatProductSubtitle = (p) => [p.presentation, p.dosage].filter(Boolean).join(' · ') || null;

// Same thumbnail as the products listing (first image, or the generic placeholder).
const productThumbnail = (p) => (
  <img
    src={p.images?.length > 0 ? p.images[0].url : '/images/no_product.png'}
    alt=""
    loading="lazy"
    style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
  />
);

const formatProductOption = (p) => ({
  value: p.id,
  label: p.name,
  subtitle: formatProductSubtitle(p),
  laboratory: p.laboratory ? { id: p.laboratory.id, name: p.laboratory.name } : null,
  icon: productThumbnail(p),
});

const InputProduct = ({
  name,
  handleChange,
  errors,
  validationRules = {},
  allowCustomValue = false,
  className,
  placeholder,
  initialKeyword = '',
  hideToggle = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState(initialKeyword);
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef(null);
  // The already-selected option (e.g. this field's initial value) — always kept present in
  // `options` even after a `replace` fetch (initial load, a new search) would otherwise drop
  // it, which used to make the selection lose its checkmark in the dropdown once reopened.
  const pinnedOptionRef = useRef(null);
  // The mount fetch (initialKeyword, often a slow multi-token designation) and a subsequent
  // user search both `replace` the options — without this, whichever happens to resolve last
  // wins, even if it was requested first, silently clobbering fresher/better results.
  const requestSeqRef = useRef(0);
  const { control, getValues } = useFormContext();

  const fetchProducts = useCallback(async (keyword, pageNum, replace) => {
    const requestId = ++requestSeqRef.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/products`, {
        withCredentials: true,
        params: { page: pageNum, size: PAGE_SIZE, keyword, prefixMatch: true },
      });
      if (requestId !== requestSeqRef.current) return;
      const content = data.content || [];
      const formatted = content.map(formatProductOption);
      setOptions(prev => {
        const base = replace ? formatted : [...prev, ...formatted];
        const pinned = pinnedOptionRef.current;
        return (pinned && !base.some(o => o.value === pinned.value)) ? [pinned, ...base] : base;
      });
      setHasMore(!data.last);
      setPage(pageNum);
    } catch (e) {
      console.error('Failed to fetch products', e);
    } finally {
      if (requestId === requestSeqRef.current) setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(() => debounce((keyword) => {
    setCurrentKeyword(keyword);
    fetchProducts(keyword, 0, true);
  }, 300), [fetchProducts]);

  useEffect(() => {
    // search-only field (hideToggle): nothing is listed before typing, so no initial fetch
    if (!hideToggle || initialKeyword) fetchProducts(initialKeyword, 0, true);
    return () => debouncedSearch.cancel();
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchProducts(currentKeyword, page + 1, false);
    }
  }, [loading, hasMore, currentKeyword, page, fetchProducts]);

  const fetchProductById = async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/products/${id}`, { withCredentials: true });
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const initializeDefaultValue = async () => {
      const elementId = getValues(name);
      if (elementId && !options.find(o => o.value === elementId)) {
        const product = await fetchProductById(elementId);
        if (product && inputRef.current) {
          const opt = formatProductOption(product);
          pinnedOptionRef.current = opt;
          inputRef.current.setExternalValue(opt);
          setOptions(prev => prev.some(p => p.value === opt.value) ? prev : [...prev, opt]);
        }
      }
    };
    initializeDefaultValue();
  }, [getValues, name]);

  useEffect(() => {
    const handleProductScanned = async (productData) => {
      if (!productData.product?.name) return;
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/products`, {
        withCredentials: true,
        params: { page: 0, size: 10, keyword: productData.product.name },
      });
      const formatted = (data.content || []).map(formatProductOption);
      const match = formatted.find(o => o.label.toLowerCase() === productData.product.name.toLowerCase());
      if (match && inputRef.current) inputRef.current.setExternalValue(match);
    };
    eventBus.on('productScanned', handleProductScanned);
    return () => eventBus.off('productScanned', handleProductScanned);
  }, []);

  // Keep whatever is currently selected (including a clear, i.e. null) pinned so it survives
  // the next replace-fetch (a new search, pagination reset...).
  const handleChangeAndPin = useCallback((option) => {
    pinnedOptionRef.current = option || null;
    handleChange?.(option);
  }, [handleChange]);

  const handleProductCreated = (created) => {
    const product = created?.product;
    if (!product) return;
    const opt = formatProductOption(product);
    pinnedOptionRef.current = opt;
    setOptions(prev => [opt, ...prev.filter(o => o.value !== opt.value)]);
    inputRef.current?.setExternalValue(opt);
    setShowCreate(false);
  };

  // When an initialKeyword (e.g. a purchase-order line's extracted designation) is set, the
  // create suggestion always proposes it, no matter what the user typed while searching —
  // typing is just for finding an existing match, not for renaming what gets created.
  const createKeyword = initialKeyword || currentKeyword;

  const createFooter = (
    <button
      type="button"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px',
        background: '#f0fdf4', border: 'none', borderTop: '1px solid #e5e7eb',
        cursor: 'pointer', color: '#15803d', fontSize: 13, fontWeight: 500,
      }}
      onClick={() => setShowCreate(true)}
    >
      <FontAwesomeIcon icon={faPlus} />
      Créer{createKeyword ? ` "${createKeyword}"` : ' un nouveau produit'}
    </button>
  );

  return (
    <div className="position-relative">
      <ImprovedSelect
        ref={inputRef}
        name={name}
        multiple={false}
        options={options}
        onChange={handleChangeAndPin}
        onInputChange={debouncedSearch}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        control={control}
        errors={errors}
        validationRules={validationRules}
        allowCustomValue={allowCustomValue}
        loading={loading}
        className={className}
        noOptionsFooter={createFooter}
        placeholder={placeholder}
        hideToggle={hideToggle}
      />
      {showCreate && (
        <CreateProductModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          defaultValues={{ status: 'MARKETED', productId: createKeyword || undefined }}
          onSave={handleProductCreated}
        />
      )}
    </div>
  );
};

export default InputProduct;
